import { useEffect } from 'react';
import { addDays, addYears, format, isAfter, isBefore, subDays } from 'date-fns';

import { FormField, FormHelp, Label, Spacer, TextButton } from '@zillow/constellation';

import type { Lease, Section } from '../../../models/index';
import { LEASE_TERMS } from '../../../constants';
import LeaseTermsListBox from './LeaseTerms/LeaseTermsListBox';
import LeaseTermsEndDate from './LeaseTerms/LeaseTermsEndDate';
import LeaseTermsStartDate from './LeaseTerms/LeaseTermsStartDate';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  hideLegalText?: boolean;
  lease: Lease;
};

export default function LeaseTerms({ section, setSectionValues, hideLegalText, lease }: Props): JSX.Element {
  useEffect(() => {
    const field = section.getField(LEASE_TERMS.FIELD_NAME.END_DATE);
    if (field) {
      field.customValidator = endDateValidator;
    }
  }, []);

  function isSelected(termFieldName: string): boolean {
    return section.getFieldValue(termFieldName) || false;
  }

  function getStartDate(): string {
    return section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE);
  }

  function endDateValidator(value: any): boolean {
    const startDate = getStartDate();
    const oneDayAfterStart = addDays(startDate, 1);
    const twoYearsAfterStart = addYears(startDate, 2);

    if (
      (startDate && value && isBefore(value, oneDayAfterStart)) ||
      (isAfter(value, twoYearsAfterStart) && !hideLegalText)
    ) {
      return false;
    }
    return true;
  }

  function onSetOneYearTermClick(): void {
    const startDate = getStartDate();
    const formattedOneYearLaterEndDate = format(subDays(addYears(startDate, 1), 1), 'MMMM D, YYYY');

    setSectionValues({ [LEASE_TERMS.FIELD_NAME.END_DATE]: formattedOneYearLaterEndDate });
  }

  function setOneYearTermLabel(): string {
    if (
      section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).hasError ||
      !section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE)
    ) {
      return 'Set to 1 year term';
    }
    const startDate = getStartDate();
    const formattedOneYearLaterEndDate = format(subDays(addYears(startDate, 1), 1), 'MMMM D, YYYY');

    return `Set to 1 year term (${formattedOneYearLaterEndDate})`;
  }

  return (
    <>
      <FormField
        marginBottom="sm"
        label={<Label size="lg">What are your lease terms?</Label>}
        control={<LeaseTermsListBox section={section} setSectionValues={setSectionValues} lease={lease} />}
      />
      {(isSelected(LEASE_TERMS.FIELD_NAME.IS_MONTH_TO_MONTH) || isSelected(LEASE_TERMS.FIELD_NAME.IS_FIXED)) && (
        <Spacer marginBottom="sm">
          <FormField
            marginBottom="xs"
            label={<Label>{LEASE_TERMS.LABEL.START_DATE}</Label>}
            error={section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).hasError}
            control={<LeaseTermsStartDate section={section} setSectionValues={setSectionValues} lease={lease} />}
            description={
              section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).hasError && (
                <FormHelp>{section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).getErrorMessage()}</FormHelp>
              )
            }
          />
          {!isSelected(LEASE_TERMS.FIELD_NAME.IS_MONTH_TO_MONTH) && (
            <Spacer marginTop="sm">
              <FormField
                marginBottom="xs"
                label={<Label>{LEASE_TERMS.LABEL.END_DATE}</Label>}
                error={section.getField(LEASE_TERMS.FIELD_NAME.END_DATE).hasError}
                control={
                  <LeaseTermsEndDate
                    hideLegalText={hideLegalText}
                    section={section}
                    setSectionValues={setSectionValues}
                    lease={lease}
                  />
                }
                description={
                  section.getField(LEASE_TERMS.FIELD_NAME.END_DATE).hasError ? (
                    <FormHelp>{section.getField(LEASE_TERMS.FIELD_NAME.END_DATE).getErrorMessage()}</FormHelp>
                  ) : (
                    !hideLegalText && <FormHelp>Leases should not exceed 2 years under applicable law</FormHelp>
                  )
                }
              />
              <TextButton
                disabled={
                  section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).hasError ||
                  !section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE)
                }
                onClick={onSetOneYearTermClick}
              >
                {setOneYearTermLabel()}
              </TextButton>
            </Spacer>
          )}
        </Spacer>
      )}
    </>
  );
}
